"use strict"
/*global PDFDocument:false, blobStream:false, operative:false*/

//PDFDocument and blobstream are referencing line 150 and 151, do not try importing the modules
// because operative library only accepts script urls

import AssignmentExport from "../../model/domain/assignment-export"
import JSZip from "jszip"
import { Canvg } from "canvg"

export default class ExportService {
  constructor($document, $window) {
    "ngInject"

    this.$document = $document
    this.$window = $window
    this._XMLSerializer = new XMLSerializer()
  }

  /**
   * Converts map of works and users, and classCode and assignment into AssignmentExports
   * @param workForStudent {Map.<string, Assignment|AssignmentWork>}
   * @param users {User[]|{name: string, id: string}[]}
   * @param classCode {ClassCode}
   * @param assignment {Assignment}
   * @param [showScore] {boolean}
   *
   * @return {AssignmentExport[]}
   */
  createAssignmentExports(
    workForStudent,
    users,
    classCode,
    assignment,
    showScore,
  ) {
    return users.map((user) => {
      let work = workForStudent.get(user.id)
      return new AssignmentExport(
        work || assignment,
        user,
        classCode,
        undefined,
        showScore,
      )
    })
  }

  /**
   * Converts map of works, map of virtual assignments, and user into AssignmentExports
   * @param worksForSingleStudent {Map.<string, AssignmentWork>}
   * @param user {User}
   * @param virtualAssignments {StudentOverviewAssignmentItem[]}
   *
   * @return {AssignmentExport[]}
   */
  createOverviewAssignmentExports(
    worksForSingleStudent,
    user,
    virtualAssignments,
  ) {
    return virtualAssignments.map((virtualAssignment) => {
      let work = worksForSingleStudent.get(virtualAssignment.assignment.id)
      return new AssignmentExport(
        work || virtualAssignment.assignment,
        user,
        "",
        undefined,
      )
    })
  }

  /**
   * @param svgDOM {SVGElement | Node}
   * @return {string}
   */
  svgDOMtoString(svgDOM) {
    const svgClone = svgDOM.cloneNode(true)

    this._replaceVerticalTabs(svgClone)

    return this._XMLSerializer.serializeToString(svgClone)
  }

  /**
   * @param {Node} node - DOM node to process
   * @private
   */
  _replaceVerticalTabs(node) {
    if (node.nodeType === 3 && node.textContent) {
      // Replace vertical tabs (0x0B) with newlines (0x0A)
      node.textContent = node.textContent.replace("\u000B", "\u000A")
    }

    // Process children recursively
    if (node.childNodes) {
      for (let i = 0; i < node.childNodes.length; i++) {
        this._replaceVerticalTabs(node.childNodes[i])
      }
    }
  }

  /**
   * Converts svgString to Data URL
   * @param svgString {string}
   * @param [height] {number}
   * @param [width] {number}
   * @return {Promise.<string>}
   */
  async svgStringToDataURL(svgString, height, width) {
    //need to change foreign object to g element for PDF to render correctly
    const updatedString = svgString.replaceAll("foreignObject", "g")

    height = height || 2008
    width = width || 1004

    let canvas = this.$document[0].createElement("canvas")
    canvas.height = height
    canvas.width = width

    let ctx = canvas.getContext("2d")

    const canvg = await Canvg.from(ctx, updatedString)
    await canvg.render()

    let png = canvas.toDataURL("image/png")
    return png
  }

  /**
   * Converts svgString to a PNG Blob via canvas without intermediate data URLs
   * @param {string} svgString
   * @param {number} [height]
   * @param {number} [width]
   * @param {Object} [overlayCoords]
   * @param {number} [overlayCoords.x]
   * @param {number} [overlayCoords.y]
   * @return {Promise.<Blob>}
   */
  async svgStringToBlob(svgString, height, width, overlayCoords) {
    // need to change foreign object to g element for PDF to render correctly
    let updatedString = svgString.replaceAll("foreignObject", "g")

    height = height || 2008
    width = width || 1004

    // add white background rectangle to SVG if not already present
    if (
      !updatedString.includes(
        '<rect x="0" y="0" width="1004" height="2008" fill="#',
      )
    ) {
      const backgroundRect = `<rect width="100%" height="100%" fill="#FFFFFF"/>`
      // insert after opening svg tag
      updatedString = updatedString.replace(
        // matches the opening <svg> tag, including any attributes
        // - `<svg` matches the literal <svg
        // - `[^>]*` matches any number of characters that are not >
        // - `>` matches the closing > of the tag
        /<svg[^>]*>/,
        (match) => `${match}${backgroundRect}`,
      )
    }

    let canvas = this.$document[0].createElement("canvas")
    canvas.height = height
    canvas.width = width

    let ctx = canvas.getContext("2d")

    const canvg = await Canvg.from(ctx, updatedString)
    await canvg.render()

    // draw optional overlay dot after SVG is rendered
    if (overlayCoords) {
      const radius = 9
      const color = "#FF0000"
      const stroke = "#000000"
      const strokeWidth = 6
      const x = overlayCoords.x
      const y = overlayCoords.y

      ctx.globalAlpha = 0.5
      // Draw stroke first so it's completely outside the circle
      ctx.beginPath()
      ctx.arc(x, y, radius + strokeWidth / 2, 0, Math.PI * 2)
      ctx.lineWidth = strokeWidth
      ctx.strokeStyle = stroke
      ctx.stroke()
      // Draw fill on top
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob)
        },
        "image/jpeg",
        0.9,
      )
    })
  }

  /**
   * Converts assignmentExport object to PDF File
   * @param assignmentExport {AssignmentExport}
   * @return {Promise.<File>}
   */
  workToPDF(assignmentExport) {
    return this.workToPDFWorker(
      assignmentExport.questionDataURLs,
      assignmentExport.studentDisplay,
      assignmentExport.assignmentDisplay,
      assignmentExport.scoreDisplays,
      assignmentExport.classCodeDisplay,
      assignmentExport.fileName,
      assignmentExport.showMetadata,
      assignmentExport.showScore,
    )
  }

  get workToPDFWorker() {
    if (!this._workToPDFWorker) {
      this._workToPDFWorker = this.$window.operative(
        function (
          questionDataURLs,
          studentDisplay,
          assignmentDisplay,
          scoreDisplays,
          classCodeDisplay,
          fileName,
          showMetadata,
          showScore,
        ) {
          let deferred = this.deferred()

          let doc = new PDFDocument()
          let stream = doc.pipe(blobStream())
          let isFirstQuestion = true

          questionDataURLs.forEach((dataURL, index) => {
            if (isFirstQuestion) {
              isFirstQuestion = false
            } else {
              doc.addPage()
            }

            if (showMetadata) {
              doc.image(dataURL, 200, 0, { scale: 0.39 }) // make room for metadata
              let scoreDisplay = scoreDisplays[index]
              let pageDisplay = `Page ${index + 1} / ${scoreDisplays.length}`

              let display = doc
                .fontSize(10)
                .text(studentDisplay, 15, 20)
                .moveDown()
                .text(assignmentDisplay)
                .moveDown()
                .text(pageDisplay)
                .moveDown()

              if (scoreDisplay && showScore) {
                display = display.text(scoreDisplay).moveDown()
              }

              display.text(classCodeDisplay)

              doc.fontSize(8).text("Printed from Classkick", 15, 700)
            } else {
              doc.image(dataURL, 0, 0, { scale: 0.6 })
            }
          })

          doc.end()

          stream.on("finish", () => {
            let pdf = stream.toBlob("application/pdf")
            let file = new File([pdf], fileName)
            deferred.fulfill(file)
          })
        },
        [
          //need to get both of these files added to CDN - the operate package only takes in scripts as dependencies
          "https://assets.classkick.com/js/pdfkit.js",
          "https://assets.classkick.com/js/blob-stream-v0.1.3.js",
        ],
      )
    }
    return this._workToPDFWorker
  }

  /**
   * @param files {File}
   * @return {Promise.<Blob>}
   */
  toZip(files) {
    let zip = new JSZip()

    files.forEach((file) => {
      zip.file(file.name, file, { base64: true })
    })

    return zip.generateAsync({ type: "blob" })
  }
}
